/**
 * Typed Firestore collection/doc references, built from @mitang/shared paths
 * and domain types. Everything the admin READS goes through here. On the free
 * tier there are no callables: privileged WRITES (user status/profile edits,
 * push_campaigns, app_config) are direct Firestore writes the security rules
 * allow for admins — see hooks/useAdminActions.ts and hooks/useCampaigns.ts.
 */
import {
  collection,
  doc,
  type CollectionReference,
  type DocumentReference,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';

import {
  COLLECTIONS,
  APP_CONFIG_DOC,
  SCRATCH_CARD_DOC,
  type User,
  type Product,
  type ActivityLog,
  type PushCampaign,
  type AppConfig,
  type Presence,
  type ScratchCard,
  type ScratchCardConfig,
} from '@mitang/shared';

import { db } from './firebase';

/** Generic converter that also injects the document id into the entity. */
function withId<T extends { id: string }>(): FirestoreDataConverter<T> {
  return {
    toFirestore: (data) => {
      // Never persist the synthetic `id`; it's the doc key.
      const { id: _id, ...rest } = data as T & { id: string };
      void _id;
      return rest as Record<string, unknown>;
    },
    fromFirestore: (snap: QueryDocumentSnapshot): T => {
      const data = snap.data() as Omit<T, 'id'>;
      return { ...(data as object), id: snap.id } as T;
    },
  };
}

/** Converter for entities without a stored `id` field (ActivityLog). */
function passthrough<T>(): FirestoreDataConverter<T> {
  return {
    toFirestore: (data) => data as Record<string, unknown>,
    fromFirestore: (snap: QueryDocumentSnapshot): T => snap.data() as T,
  };
}

export const usersCol = collection(db, COLLECTIONS.users).withConverter(
  withId<User>(),
) as CollectionReference<User>;

export const productsCol = collection(db, COLLECTIONS.products).withConverter(
  withId<Product>(),
) as CollectionReference<Product>;

export const pushCampaignsCol = collection(
  db,
  COLLECTIONS.pushCampaigns,
).withConverter(withId<PushCampaign>()) as CollectionReference<PushCampaign>;

/** ActivityLog has no `id` field in the shared type; expose id separately. */
export type ActivityLogWithId = ActivityLog & { id: string };

const activityConverter: FirestoreDataConverter<ActivityLogWithId> = {
  toFirestore: (data) => {
    const { id: _id, ...rest } = data as ActivityLogWithId;
    void _id;
    return rest as Record<string, unknown>;
  },
  fromFirestore: (snap: QueryDocumentSnapshot): ActivityLogWithId => {
    const data = snap.data() as ActivityLog;
    return { ...data, id: snap.id };
  },
};

export const activityLogsCol = collection(
  db,
  COLLECTIONS.activityLogs,
).withConverter(activityConverter) as CollectionReference<ActivityLogWithId>;

export const appConfigConverter = passthrough<AppConfig>();

export const appConfigDoc = doc(
  db,
  COLLECTIONS.appConfig,
  APP_CONFIG_DOC,
).withConverter(appConfigConverter) as DocumentReference<AppConfig>;

/**
 * Live presence — one doc per user (doc id = uid). The `uid` is also stored
 * inside the doc, so a passthrough converter is enough for admin reads.
 */
export const presenceCol = collection(db, COLLECTIONS.presence).withConverter(
  passthrough<Presence>(),
) as CollectionReference<Presence>;

/* ------------------------------ scratch & win ----------------------------- */

/** `scratch_cards/{round}_{uid}` — every dealer's card, written by the worker. */
export const scratchCardsCol = collection(db, COLLECTIONS.scratchCards).withConverter(
  withId<ScratchCard>(),
) as CollectionReference<ScratchCard>;

/** `app_config/scratchCard` — the on/off switch, current round and prize table. */
export const scratchCardConfigDoc = doc(
  db,
  COLLECTIONS.appConfig,
  SCRATCH_CARD_DOC,
).withConverter(passthrough<ScratchCardConfig>()) as DocumentReference<ScratchCardConfig>;
